import { Col, Container, Form, Row } from 'react-bootstrap'
import React, { useEffect, useState } from 'react'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { Controller, useForm } from 'react-hook-form'
import { Button } from '../../../components/Button/Button'
import { AccountConfig } from '../../../models/Account'

export const ConfirmationNoticeSettings: React.FC = () => {
  type FormValues = {
    message: string
    enabled: boolean
  }

  const [dataExportConfirmationEnterpriseConfigured, setDataExportConfirmationEnterpriseConfigured] = useState<boolean>(false)
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const defaultValues = {
    message: '',
    enabled: false,
  }

  const [updateAccount, { isLoading: accountUpdating }] = useUpdateAccountMutation()

  const { control, handleSubmit, reset } = useForm<FormValues>({ defaultValues })

  const onSave = async (data: FormValues) => {
    if (!account) {
      return
    }

    const accountConfig: AccountConfig = {
      ...account.data.config,
      integrationExportConfirmationConfig: {
        message: data.message,
        enabled: account.data.config.integrationExportConfirmationConfig.enabled,
      },
    }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  const onSwitch = async (checked: boolean) => {
    if (!account) {
      return
    }

    const accountConfig: AccountConfig = {
      ...account.data.config,
      integrationExportConfirmationConfig: {
        message: account.data.config.integrationExportConfirmationConfig.message,
        enabled: checked,
      },
    }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    })
  }

  useEffect(() => {
    if (!account) {
      return
    }
    setDataExportConfirmationEnterpriseConfigured(account.data.enterpriseConfig.complianceConfig.integrationExportConfirmationConfig != null || false)
    reset({
      message:
        account.data.effectiveConfig.complianceConfig.integrationExportConfirmationConfig?.message ??
        account.data.config.integrationExportConfirmationConfig.message,
      enabled:
        account.data.effectiveConfig.complianceConfig.integrationExportConfirmationConfig?.enabled ??
        account.data.config.integrationExportConfirmationConfig.enabled,
    })
  }, [account, reset])

  return (
    <Container fluid>
      <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
        <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
          <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
          <span className='zocks-text-gray-900'>
            {' '}
            Confirmation dialogs can optionally be enforced when user save data to third-party systems (CRM, Financial Planning, etc.). The dialogs require
            acknowledgment before proceeding and display the custom message configured below.{' '}
          </span>
        </Col>
      </Row>
      {dataExportConfirmationEnterpriseConfigured ? (
        <Row className='zocks-mb-2 p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>Confirmation Notice on Data Exports settings are managed at the enterprise level.</span>
            </div>
          </Col>
        </Row>
      ) : null}
      <Row className='zocks-mb-2 gap-3 p-0 align-items-center'>
        <Col className='p-0 d-flex gap-4'>
          <form onSubmit={handleSubmit(onSave)} className='w-100'>
            <Controller
              name='enabled'
              control={control}
              render={({ field }) => (
                <Row className='zocks-mb-2'>
                  <Col xs='auto'>
                    <ScopedBlock
                      scope={{
                        scope: 'account:update',
                        superScope: ['account:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Switch
                        disabled={!account || dataExportConfirmationEnterpriseConfigured || isAccountFetching || accountUpdating}
                        onChange={(e) => onSwitch(e.target.checked)}
                        checked={field.value}
                        id={field.name}
                      />
                    </ScopedBlock>
                  </Col>
                  <Col xs='auto' className='ms-2'>
                    <div className='paragraph-medium'>I want to enforce data export confirmations</div>
                  </Col>
                </Row>
              )}
            />

            <Controller
              name='message'
              control={control}
              render={({ field, fieldState }) => (
                <Row>
                  <Col>
                    <ScopedBlock
                      scope={{
                        scope: 'account:update',
                        superScope: ['account:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Control
                        id={field.name}
                        as='textarea'
                        rows={3}
                        placeholder='Customize'
                        {...field}
                        disabled={!account || dataExportConfirmationEnterpriseConfigured || isAccountFetching || accountUpdating}
                      />
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </ScopedBlock>
                  </Col>
                </Row>
              )}
            />
            <ScopedBlock
              scope={{
                scope: 'account:update',
                superScope: ['account:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Button
                variant='primary'
                type='submit'
                disabled={!account || dataExportConfirmationEnterpriseConfigured || isAccountFetching || accountUpdating}
                className='mt-3'
              >
                Save Changes
              </Button>
            </ScopedBlock>
          </form>
        </Col>
      </Row>
    </Container>
  )
}
